import { RewardType } from '@wix/ambassador-loyalty-v1-reward/types';
import { loyaltyUouClick } from '@wix/bi-logger-loyalty-uou/v2';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { CheckSmall as CheckSmallIcon } from '@wix/wix-ui-icons-common/on-stage';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import React, { useState } from 'react';
import { Badge, BadgePriority, Divider, Text } from '@wix/wix-ui-tpa/cssVars';

import { DataHook } from '../../../types/data-hook';
import { SimpleReward } from '../../../types/domain';
import { isRewardAvailable } from '../../../utils';
import settingsParams from '../settingsParams';
import { RewardsLayout } from '../stylesParams';
import { ModalType } from './hocs/modal-context';
import { useAccount, useAppInstallStatus, useCouponsActions, useModal } from './hooks';
import { classes } from './list-item.st.css';
import { ListItemButton } from './list-item-button';

interface ListItemProps {
  reward: SimpleReward;
  isLastReward: boolean;
}

export const ListItem: React.FC<ListItemProps> = ({ reward, isLastReward }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const settings = useSettings();
  const { showModal } = useModal();
  const { isReferralsAppInstalled, isLoyaltyAppInstalled } = useAppInstallStatus();
  const { redeemCoupon } = useCouponsActions();
  const { account } = useAccount();
  const [copied, setCopied] = useState(false);
  const { isMobile } = useEnvironment();
  const bi = useBi();
  const showDivider =
    !isLastReward &&
    styles
      .getStylesForAllBreakpoints()
      .some((breakpointStyles) => breakpointStyles.numbers.rewardsLayout === RewardsLayout.List);
  const showBadgeAboveTitle =
    styles
      .getStylesForAllBreakpoints()
      .some((breakpointStyles) => breakpointStyles.numbers.rewardsLayout === RewardsLayout.Cards) || isMobile;
  const showBadgeBehindTitle = styles
    .getStylesForAllBreakpoints()
    .some((breakpointStyles) => breakpointStyles.numbers.rewardsLayout === RewardsLayout.List);

  const isOnlyReferralsAppInstalled = isReferralsAppInstalled && !isLoyaltyAppInstalled;
  const isCheckoutDiscountReward = [RewardType.DISCOUNT_AMOUNT, RewardType.SPI_DISCOUNT_AMOUNT].includes(reward.type!);

  const pointsBalance = account.pointsBalance ?? 0;
  const requiredPoints = reward.requiredPoints ?? 0;
  const hasEnoughPoints = isRewardAvailable({ pointsBalance, requiredPoints });

  const itemTitleTag = settings.get(settingsParams.rewardsRewardTitleTag);
  const itemDescriptionTag = settings.get(settingsParams.rewardsRewardTextTag);

  const handleCopyCodeClick = () => {
    navigator.clipboard.writeText(reward.couponCode!);

    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 5000);

    bi.report(
      loyaltyUouClick({
        value: 'copy_coupon_code',
        origin: 'my_rewards_page',
      }),
    );
  };

  const handleGetRewardClick = () => {
    showModal({
      type: ModalType.GET_COUPON,
      props: {
        reward,
        onConfirm: () => {
          bi.report(
            loyaltyUouClick({
              value: 'redeem_coupon_reward',
              origin: 'popup',
            }),
          );
          redeemCoupon(reward);
        },
      },
    });

    bi.report(
      loyaltyUouClick({
        value: 'redeem_coupon_reward',
        origin: 'my_rewards_page',
      }),
    );
  };

  const useSecondaryBadgeForReferrals = !isOnlyReferralsAppInstalled;

  const badge = reward.isReferralReward ? (
    <Badge
      className={useSecondaryBadgeForReferrals ? classes.secondaryBadge : classes.primaryBadge}
      priority={useSecondaryBadgeForReferrals ? BadgePriority.light : BadgePriority.default}
    >
      {t('app.reward-description.referral-program')}
    </Badge>
  ) : isCheckoutDiscountReward && hasEnoughPoints ? (
    <Badge className={classes.primaryBadge} icon={<CheckSmallIcon aria-hidden />}>
      {t('app.reward-description.available-in-checkout')}
    </Badge>
  ) : null;

  const showItemButton = !isCheckoutDiscountReward || !hasEnoughPoints;

  return (
    <li>
      <div data-hook={DataHook.RewardsListItem} className={classes.item}>
        <div className={classes.contentContainer}>
          <div className={classes.contentContainerInner}>
            <Text tagName={itemTitleTag} data-hook={DataHook.RewardsListItemTitle} className={classes.title}>
              {reward.title}
              {showBadgeBehindTitle && badge && <div className={classes.badgeBehindTitle}>{badge}</div>}
            </Text>
            {showBadgeAboveTitle && badge && <div className={classes.badgeAboveTitle}>{badge}</div>}
          </div>
          <Text
            tagName={itemDescriptionTag}
            data-hook={DataHook.RewardsListItemDescription}
            className={classes.description}
          >
            {reward.description}
          </Text>
        </div>
        {showItemButton && (
          <div className={classes.buttonContainer}>
            <ListItemButton
              showCopied={copied}
              reward={reward}
              onClick={reward.couponActivated ? handleCopyCodeClick : handleGetRewardClick}
              hasEnoughPoints={hasEnoughPoints}
            />
          </div>
        )}
      </div>
      {showDivider && (
        <div aria-hidden>
          <Divider className={classes.divider} />
        </div>
      )}
    </li>
  );
};
